import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios'

export interface RequestParams {
  method: Method
  url: string
  headers?: Record<string, string>
  onRetry?: () => any
  data?: any
  apiKey?: string
  contentType?: string
}

export interface FailedResponse {
  success: boolean
  message: string
}

export function isFailedResponse(item: unknown): item is FailedResponse {
  return (item as FailedResponse).success === false
}

const defaultContentType = 'application/json'

export type WithQuota<T> = T & { remainingQuota?: number }

function getQuotaFromHeader(response: AxiosResponse<any, any>): number | undefined {
  const quotaVal = response.headers['x-remaining-quota']
  if (quotaVal != null) {
    return (typeof quotaVal === 'string') ? parseInt(quotaVal) : quotaVal as number
  }
  return undefined
}

export async function requestUrl<T>({ method, url, headers, data, apiKey, contentType }: RequestParams): Promise<WithQuota<T> | null>  {
  headers = headers ?? {}
  headers['Content-Type'] = contentType ?? defaultContentType
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`
  }
  try {
    const config: AxiosRequestConfig<any> = {
      method,
      url,
      headers,
      data,
      timeout: 360_000
    }
    const response = await axios(config)
    const remainingQuota = getQuotaFromHeader(response)
    if (remainingQuota != null) {
      return { ...response.data, remainingQuota } as WithQuota<T>
    }
    return response.data as WithQuota<T>
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const remainingQuota = getQuotaFromHeader(error.response)
      if (remainingQuota != null) {
        return { ...error.response.data, remainingQuota } as WithQuota<T>
      }
      return error.response.data as WithQuota<T>
    }
    const msg = `Request failed. URL: [${method}] ${url}\nError: `
    console.error(msg, error)
    return null
  }
}

export async function submitEmail(email: string) {
  const response = await requestUrl({
    method: 'POST',
    url: '/email',
    data: { email }
  })
  return response
}

interface SignInRequest {
  username: string
  password: string
}

export interface SignInResponse {
  success: boolean
  message?: string
}

export async function authenticate({ username, password }: SignInRequest): Promise<SignInResponse> {
  const failedAuth = { success: false, message: 'Authentication failed' }
  const response = await requestUrl<SignInResponse>({
    method: 'POST',
    url: '/api/authenticate',
    data: { username, password }
  })
  return response ?? failedAuth
}

export async function signOut() {
  const response = await requestUrl<SignInResponse>({
    method: 'POST',
    url: '/api/signout'
  })
}

export interface AuthCheckResponse {
  success: boolean
  message?: string
  username?: string
  first?: string
  last?: string
  email?: string
}

export async function checkAuth(): Promise<AuthCheckResponse> {
  const failedAuth = { success: false, message: 'Authentication check failed' }
  const response = await requestUrl<AuthCheckResponse>({
    method: 'GET',
    url: '/api/authcheck'
  })
  return response ?? failedAuth
}

export interface SignUpRequest {
  username: string
  first: string
  last: string
  password: string
  email: string
  code: string
}

export interface SignUpResponse {
  success: boolean
  message?: string
}

export async function signUp({ username, first, last, password, email, code }: SignUpRequest): Promise<SignUpResponse> {
  const failedSignup = { success: false, message: 'Could not sign up. Check invite code.' }
  const response = await requestUrl<AuthCheckResponse>({
    method: 'POST',
    url: '/api/register',
    data: {
      username,
      first,
      last,
      password,
      code,
      email
    }
  })
  console.log(response)
  return response ?? failedSignup
}

export type SubLevel = 0 | 1 | 2 | 3 | 4

export interface SubscriptionInfo {
  subscriptionActive: boolean
  subscriptionLevel: SubLevel
  subscriptionExpiresAt: number
  remainingQuota: number
}

export interface SubscriptionInfoResponse extends SubscriptionInfo {
  success: boolean
}

export async function getSubscriptionInfo(username: string): Promise<SubscriptionInfoResponse | FailedResponse> {
  const failed = { success: false, message: 'Could not get subscription info.' }
  const response = await requestUrl<SubscriptionInfoResponse>({
    method: 'POST',
    url: '/api/subscription-info',
    data: {
      username
    }
  })
  return response ?? failed
}

export async function requestAccess({ email, referralCode }: { email: string, referralCode: string }): Promise<SignUpResponse> {
  const failedSignup = { success: false, message: 'Could not request access.' }
  const response = await requestUrl<{ success: boolean }>({
    method: 'POST',
    url: '/api/request',
    data: {
      email,
      referralCode
    }
  })
  return response ?? failedSignup
}

export interface AccessRequest {
  email: string
  timestamp: number
  emailSentAt?: number
}

export async function getAccessRequests(): Promise<{ success: boolean, requests: AccessRequest[] } | FailedResponse> {
  const failed = { success: false, message: 'Could not get access requests.' }
  const response = await requestUrl<{ success: boolean, requests: AccessRequest[] }>({
    method: 'GET',
    url: '/api/admin/access-requests'
  })
  return response ?? failed
}

export async function getNotSignedUp(): Promise<{ success: boolean, requests: string[] } | FailedResponse> {
  const failed = { success: false, message: 'Could not get not signed up.' }
  const response = await requestUrl<{ success: boolean, requests: string[] }>({
    method: 'GET',
    url: '/api/admin/not-signed-up'
  })
  return response ?? failed
}

export interface EmailMessage {
  to: string
  from: string
  subject: string
  text: string
  html: string
}

export async function sendEmail(message: EmailMessage): Promise<{ success: boolean, result?: any } | FailedResponse> {
  const failed = { success: false, message: 'Could not send email.' }
  const response = await requestUrl<{ success: boolean }>({
    method: 'POST',
    url: '/api/admin/send-email',
    data: message
  })
  return response ?? failed
}

export interface ChatResponse {
  success: boolean
  id: string
  response?: string
  message?: string
  analysis?: string
  remainingQuota?: number
}

export async function sendChat(input: string, id: string): Promise<ChatResponse> {
  const failedChat = { success: false, id, message: 'Could not send chat message' }
  const response = await requestUrl<ChatResponse>({
    method: 'POST',
    url: '/api/chat',
    data: {
      id,
      message: input
    }
  })
  return response ?? failedChat
}

export async function continueChat(input: string, id: string): Promise<ChatResponse> {
  const failedChat = { success: false, id, message: 'Could not send chat message' }
  const response = await requestUrl<ChatResponse>({
    method: 'POST',
    url: '/api/continue-chat',
    data: {
      id,
      message: input
    }
  })
  return response ?? failedChat
}

export async function getChatTitle(input: string, id: string): Promise<ChatResponse> {
  const failedChat = { success: false, id, message: 'Could not send chat message' }
  console.log({
      id,
      message: input
    })
  const response = await requestUrl<ChatResponse>({
    method: 'POST',
    url: '/api/get-chat-title',
    data: {
      id,
      message: input
    }
  })
  return response ?? failedChat
}

export interface FullTicker {
  symbol: string
  name: string
  marketCap: number
  country: string
  industry: string
  sector: string
}

export interface StockListResponse {
  success: boolean
  response: Record<string, FullTicker>
}

export async function getStockList(): Promise<StockListResponse> {
  const failedList = { success: false, response: {} as Record<string, FullTicker> }
  const response = await requestUrl<StockListResponse>({
    method: 'GET',
    url: '/api/stock-list'
  })
  return response ?? failedList
}

export function startEvents(id: string): EventSource {
  return new EventSource(`/api/events/${id}`)
}

export interface IncomeReport {
  fiscalDateEnding: string
  reportedCurrency: string
  grossProfit: number
  totalRevenue: number
  costOfRevenue: number
  costofGoodsAndServicesSold: number
  operatingIncome: number
  sellingGeneralAndAdministrative: number
  researchAndDevelopment: number
  operatingExpenses: number
  investmentIncomeNet: number
  netInterestIncome: number
  interestIncome: number
  interestExpense: number
  nonInterestIncome: number
  otherNonOperatingIncome: number
  depreciation: number
  depreciationAndAmortization: number
  incomeBeforeTax: number
  incomeTaxExpense: number
  interestAndDebtExpense: number
  netIncomeFromContinuingOperations: number
  comprehensiveIncomeNetOfTax: number
  ebit: number
  ebitda: number
  netIncome: number
}

export interface IncomeStatementResponse {
  success: boolean
  data: IncomeReport[]
}

export async function getIncomeStatement(id: string, symbol: string, timestamp: number): Promise<IncomeReport[] | undefined> {
  const response = await requestUrl<IncomeStatementResponse>({ method: 'POST', url: '/api/fin/income-statement', data: { id, symbol, timestamp } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export interface SummaryResponse {
  success: boolean
  data: string | undefined
}

export async function getIncomeStatementSummary(id: string, symbol: string, timestamp: number): Promise<string | undefined> {
  const response = await requestUrl<SummaryResponse>({ method: 'POST', url: '/api/fin/income-statement-summary', data: { id, symbol, timestamp } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export interface CashReport {
  fiscalDateEnding: string
  reportedCurrency: string
  operatingCashflow: number
  paymentsForOperatingActivities: number
  proceedsFromOperatingActivities: number
  changeInOperatingLiabilities: number
  changeInOperatingAssets: number
  depreciationDepletionAndAmortization: number
  capitalExpenditures: number
  changeInReceivables: number
  changeInInventory: number
  profitLoss: number
  cashflowFromInvestment: number
  cashflowFromFinancing: number
  proceedsFromRepaymentsOfShortTermDebt: number
  paymentsForRepurchaseOfCommonStock: number
  paymentsForRepurchaseOfEquity: number
  paymentsForRepurchaseOfPreferredStock: number
  dividendPayout: number
  dividendPayoutCommonStock: number
  dividendPayoutPreferredStock: number
  proceedsFromIssuanceOfCommonStock: number
  proceedsFromIssuanceOfLongTermDebtAndCapitalSecuritiesNet: number
  proceedsFromIssuanceOfPreferredStock: number
  proceedsFromRepurchaseOfEquity: number
  proceedsFromSaleOfTreasuryStock: number
  changeInCashAndCashEquivalents: number
  changeInExchangeRate: number
  netIncome: number
}

export interface CashFlowStatementResponse {
  success: boolean
  data: CashReport[]
}

export async function getCashFlowStatement(id: string, symbol: string, timestamp: number): Promise<CashReport[] | undefined> {
  const response = await requestUrl<CashFlowStatementResponse>({ method: 'POST', url: '/api/fin/cash-flow-statement', data: { id, symbol, timestamp } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export async function getCashFlowStatementSummary(id: string, symbol: string, timestamp: number): Promise<string | undefined> {
  const response = await requestUrl<SummaryResponse>({ method: 'POST', url: '/api/fin/cashflow-statement-summary', data: { id, symbol, timestamp } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export interface BalanceReport {
  fiscalDateEnding: string
  reportedCurrency: string
  totalAssets: number
  totalCurrentAssets: number
  cashAndCashEquivalentsAtCarryingValue: number
  cashAndShortTermInvestments: number
  inventory: number
  currentNetReceivables: number
  totalNonCurrentAssets: number
  propertyPlantEquipmentNet: number
  accumulatedDepreciationAmortizationPPE: number
  intangibleAssets: number
  intangibleAssetsExcludingGoodwill: number
  goodwill: number
  investments: number
  longTermInvestments: number
  shortTermInvestments: number
  otherCurrentAssets: number
  otherNonCurrentAssets: number
  totalLiabilities: number
  totalCurrentLiabilities: number
  currentAccountsPayable: number
  deferredRevenue: number
  currentDebt: number
  shortTermDebt: number
  totalNonCurrentLiabilities: number
  capitalLeaseObligations: number
  longTermDebt: number
  currentLongTermDebt: number
  longTermDebtNoncurrent: number
  shortLongTermDebtTotal: number
  otherCurrentLiabilities: number
  otherNonCurrentLiabilities: number
  totalShareholderEquity: number
  treasuryStock: number
  retainedEarnings: number
  commonStock: number
  commonStockSharesOutstanding: number
}

export interface BalanceReportResponse {
  success: boolean
  data: BalanceReport[]
}

export async function getBalanceSheet(id: string, symbol: string, timestamp: number): Promise<BalanceReport[] | undefined> {
  const response = await requestUrl<BalanceReportResponse>({ method: 'POST', url: '/api/fin/balance-sheet', data: { id, symbol, timestamp } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export async function getBalanceSheetSummary(id: string, symbol: string, timestamp: number): Promise<string | undefined> {
  const response = await requestUrl<SummaryResponse>({ method: 'POST', url: '/api/fin/balance-sheet-summary', data: { id, symbol, timestamp } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export interface NewsSummary {
  id: string
  timestamp: number
  hash: string
  links: string[]
  summary: string
}

export interface AnythingLLMDocument {
  id: string
  url: string
  title: string
  docAuthor: string
  description: string
  docSource: string
  chunkSource: string
  published: string
  wordCount: number
  pageContent: string
  token_count_estimate: number
  location: string
}

export interface LinkSummary {
  timestamp: number
  symbols: string[]
  url: string
  title?: string
  document: Partial<AnythingLLMDocument>
  summary: string
}

export interface AllNews {
  newsSummary: NewsSummary
  links: LinkSummary[]
}

export interface AllNewsResponse {
  success: boolean
  data: AllNews
}

export async function getAllNews(id: string, symbol: string): Promise<AllNews | undefined> {
  const response = await requestUrl<AllNewsResponse>({ method: 'POST', url: '/api/fin/all-news', data: { id, symbol } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}

export interface LatestSummary {
  symbol: string
  timestamp: number
  summary: string
}

export interface LatestSummaryResponse {
  success: boolean
  data: LatestSummary[]
}

export async function getLatestSummaries(count?: number): Promise<LatestSummary[] | undefined> {
  const response = await requestUrl<LatestSummaryResponse>({ method: 'POST', url: '/api/fin/latest', data: { count } })
  if (response != null && response.success) {
    return response.data
  } else {
    return undefined
  }
}
